import React, { useEffect, useState } from 'react';
import { getRotationWindowSeconds } from '../utils/rotatingQr';
import './AnimatedQrFrame.css';

/**
 * Live frame for ticket QRs: spinning silver border + a thick red sweep
 * across the code + a caption below with the live clock, zone, and (while
 * rotation is on) the countdown to the next code.
 *
 * SCAN SAFETY: nothing here ever moves the QR's own pixels — effects live on
 * the frame and a sweep only, so gate scanners read the code exactly as
 * before (see scripts/qr-art-scancheck.js).
 */
const AnimatedQrFrame = ({ ticket, size = 240, children, caption = true }) => {
  const seeded = !!ticket?.rotation_seed;
  const windowSeconds = seeded ? getRotationWindowSeconds(ticket) : null;

  const [remaining, setRemaining] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!windowSeconds) return undefined;
    const update = () => {
      const nowSec = Date.now() / 1000;
      setRemaining(Math.max(1, Math.ceil(windowSeconds - (nowSec % windowSeconds))));
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [windowSeconds]);

  const frameSize = size + 30; // 5px spinning border + 10px card padding each side
  const mm = remaining == null ? '' : `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;
  const timeLabel = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const zoneLabel = Intl.DateTimeFormat().resolvedOptions().timeZone?.split('/').pop()?.replace(/_/g, ' ') || '';

  return (
    <div className="aqf" style={{ width: frameSize }}>
      <div className="aqf-frame" style={{ width: frameSize, height: frameSize }}>
        <div className="aqf-spin" aria-hidden="true" />
        <div className="aqf-inner" style={{ borderRadius: 21 }}>
          {children}
          {/* Same red sweep the app shows — pure decoration (enforcement is
              server-side), so it rides every framed QR whether or not the
              rotation toggle is on. Thickness scales with QR size to stay
              inside the jsQR-proven envelope (scripts/qr-art-scancheck.js). */}
          <div
            className="aqf-scanline"
            style={{ height: Math.max(6, Math.round(size * 0.043)) }}
            aria-hidden="true"
          />
          <div className="aqf-shine" aria-hidden="true" />
        </div>
      </div>
      {caption && (
        <div className="aqf-caption">
          <span className="aqf-live-dot" aria-hidden="true" />
          <span>LIVE · {timeLabel}{zoneLabel ? ` · ${zoneLabel}` : ''}{seeded && mm ? ` · new code in ${mm}` : ''}</span>
        </div>
      )}
    </div>
  );
};

export default AnimatedQrFrame;
